/**
 * Backend stores complex field values as a JSON envelope when Value/DisplayValue differ:
 * `{"value":"42","display":"Engineering"}`. Prefers "display", falls back to "value", then raw string.
 * @registry Unwraps a value/display JSON envelope to its human-readable display string.
 */
export const unwrapDisplayValue = (val: string): string => {
  // Cheap shape check before parsing — most values are plain strings.
  // Trigger on either "display" or "value" key so the envelope is also
  // unwrapped when DisplayValue happens to be absent.
  if (
    !val.startsWith('{') ||
    !val.endsWith('}') ||
    (!val.includes('"display"') && !val.includes('"value"'))
  ) {
    return val;
  }
  try {
    const parsed = JSON.parse(val) as {value?: unknown; display?: unknown};
    if (typeof parsed.display === 'string' && parsed.display) {
      return parsed.display;
    }
    if (typeof parsed.value === 'string') {
      return parsed.value;
    }
  } catch {
    // Not JSON — fall through.
  }
  return val;
};

/**
 * Backend audit `changes` blobs sometimes serialize scalar values as JSON strings (e.g. `"sdf"`
 * instead of `sdf`). Peels one layer of wrapping; non-quoted strings pass through untouched.
 */
const unwrapJsonScalar = (val: string): string => {
  if (val.length < 2 || !val.startsWith('"') || !val.endsWith('"')) {
    return val;
  }
  try {
    const parsed = JSON.parse(val);
    if (typeof parsed === 'string') {
      return parsed;
    }
  } catch {
    // Not a JSON-stringified scalar — fall through.
  }
  return val;
};

/**
 * Unified empty-cell rendering for audit diff cells. null/undefined/'' → ''.
 * @registry Formats an audit diff cell value, unwrapping JSON envelopes and normalizing empties.
 */
export const formatAuditValue = (val?: unknown): string => {
  if (val === undefined || val === null || val === '') {
    return '';
  }
  if (typeof val === 'string') {
    // Peel JSON-string wrapping first so downstream envelope detection sees
    // the real payload (`"{\"value\":...}"` → `{"value":...}`).
    return unwrapDisplayValue(unwrapJsonScalar(val));
  }
  return String(val);
};

/**
 * Null-normalizing equality: null/undefined/'' are all the same "unset" value.
 * @registry Compares two audit values for equality, treating null/undefined/'' as equal.
 */
export const auditValuesEqual = (a?: unknown, b?: unknown): boolean => {
  const norm = (v: unknown) => (v === null || v === undefined ? '' : v);
  return norm(a) === norm(b);
};
